import { logger } from "@/lib/logger";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSiteInvoices } from "@/hooks/useSiteInvoices";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { GenerateInvoiceDialog } from "./GenerateInvoiceDialog";
import { FileText, CheckCircle, Mail, Plus, AlertCircle, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface InvoiceManagerProps {
  trialSiteId?: string;
}

export function InvoiceManager({ trialSiteId }: InvoiceManagerProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useSiteInvoices({
    trialSiteId
  });
  const { updateInvoiceStatus, isUpdating } = useSiteInvoices({ trialSiteId });
  const { toast } = useToast();
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  
  const invoices = data?.invoices || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'sent': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const handleStatusChange = (invoiceId: string, newStatus: 'draft' | 'sent' | 'paid' | 'cancelled') => {
    updateInvoiceStatus({ id: invoiceId, status: newStatus });
  };

  const handleSendEmail = async (invoiceId: string) => {
    setSendingEmail(invoiceId);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-invoice-notification', {
        body: { invoice_id: invoiceId }
      });

      if (error) throw error;

      toast({
        title: "Email sent successfully",
        description: `Invoice notification sent to ${data.recipient}`,
      });

      // Invalidate queries to refresh data - NO full page reload!
      queryClient.invalidateQueries({ queryKey: ['site-invoices'] });
    } catch (error: unknown) {
      logger.error('Error sending invoice email:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send invoice email";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSendingEmail(null);
    }
  };

  const handleGenerateSuccess = () => {
    // Invalidate queries to refresh data - NO full page reload!
    queryClient.invalidateQueries({ queryKey: ['site-invoices'] });
    setShowGenerateDialog(false);
  };

  // Error state with retry
  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load invoices</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{error instanceof Error ? error.message : 'Unknown error occurred'}</span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted rounded" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice Management
            </CardTitle>
            <Button size="sm" onClick={() => setShowGenerateDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Generate Invoice
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No invoices yet. Generate your first invoice from billable events.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Sponsor</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Trial</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-mono font-semibold">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {invoice.sponsor?.name || 'N/A'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {invoice.trial_site?.name || 'N/A'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {invoice.trial?.trial_title?.substring(0, 40) || 'N/A'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(invoice.billing_period_start).toLocaleDateString()} - 
                        {new Date(invoice.billing_period_end).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-semibold text-primary">
                        {invoice.currency === 'USD' ? '$' : invoice.currency}
                        {Number(invoice.total_amount || 0).toLocaleString(undefined, { 
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(invoice.status === 'draft' || invoice.status === 'sent') && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleSendEmail(invoice.id)}
                              disabled={sendingEmail === invoice.id}
                            >
                              <Mail className="h-3 w-3 mr-1" />
                              {sendingEmail === invoice.id ? 'Sending...' : 'Email'}
                            </Button>
                          )}
                          {invoice.status === 'sent' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleStatusChange(invoice.id, 'paid')}
                              disabled={isUpdating}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <GenerateInvoiceDialog 
        open={showGenerateDialog}
        onOpenChange={setShowGenerateDialog}
        onSuccess={handleGenerateSuccess}
      />
    </>
  );
}
